/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * useOrganizerEvents
 * ------------------
 * Every event this organizer can see, for the list screen.
 *
 * No `where` clause names the organization: RLS already restricts `event` to
 * rows owned through `creator_organization_id`, so the filter is the database's
 * and cannot be forgotten or spoofed from the client. The organization id is
 * still a query key, so switching organizations refetches rather than showing
 * the previous one's events.
 *
 * Sorted newest-start-first because an organizer's attention is on what is
 * coming up, and an event with no start date sorts last rather than being
 * dropped — a draft saved before the dates were filled in is exactly the row
 * they are most likely to be looking for.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { readLookup } from '../components/athlohive/lookupOptions';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/** Sortable instant for a row, or -Infinity when it has no usable start. */
function startsAt(event) {
  const parsed = event?.start_datetime ? new Date(event.start_datetime).getTime() : NaN;
  return Number.isNaN(parsed) ? -Infinity : parsed;
}

export default function useOrganizerEvents(organizationId) {
  const query = useQuery({
    queryKey: ['organizerEvents', organizationId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall('/arcee/application/table/fetch/', {
        method: 'POST',
        signal,
        body: JSON.stringify({
          table_name: 'event',
          organization_id: Number(envConfig.organizationId),
          application_id: Number(envConfig.applicationId),
          page_size: 200,
        }),
      });
      if (!resp.ok) throw new Error('Could not load your events.');
      return readLookup(await resp.json());
    },
    enabled: organizationId != null,
    staleTime: envConfig.staleTimeMs,
  });

  const events = useMemo(() => {
    const rows = query.data?.rows;
    if (!Array.isArray(rows)) return [];
    return [...rows].sort((left, right) => startsAt(right) - startsAt(left));
  }, [query.data]);

  return {
    events,
    isLoading: organizationId != null && query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
